import { useCallback } from 'react'
import { useRraasiCoinStore } from '../stores/rraasiCoinStore'
import { useSubscriptionStore } from '../stores/subscriptionStore'
import { validateFeatureAccess, getFeatureCost } from '../config/featureCosts'
import { toast } from 'react-hot-toast'

export const useRraasiCoins = () => {
  const { 
    balance, 
    transactions, 
    addCoins, 
    spendCoins, 
    hasEnoughCoins,
    getBalance,
    getTransactionHistory 
  } = useRraasiCoinStore()
  
  const { isSubscriptionActive } = useSubscriptionStore()

  // Check if user can access a specific feature
  const canAccessFeature = useCallback((featureId: string): { 
    canAccess: boolean; 
    reason?: string; 
    cost?: number;
    needsCoins: boolean;
  } => {
    const feature = getFeatureCost(featureId)
    if (!feature) {
      return { 
        canAccess: false, 
        reason: 'Feature not found',
        needsCoins: false
      }
    }

    const validation = validateFeatureAccess(featureId, balance.total, isSubscriptionActive())
    
    return {
      ...validation,
      needsCoins: !isSubscriptionActive() || !feature.subscriptionUnlimited
    }
  }, [balance.total, isSubscriptionActive])

  // Use coins for a feature
  const useFeature = useCallback((featureId: string): boolean => {
    const feature = getFeatureCost(featureId)
    if (!feature) {
      toast.error('Feature not found')
      return false
    }

    const access = canAccessFeature(featureId)
    
    if (!access.canAccess) {
      if (access.needsCoins) {
        toast.error(access.reason || 'Insufficient coins')
        
        // Show upgrade suggestion
        setTimeout(() => {
          toast.error(
            `Upgrade your subscription to get unlimited access to ${feature.name}!`,
            { duration: 4000 }
          )
        }, 1000)
      } else {
        toast.error(access.reason || 'Cannot access this feature')
      }
      return false
    }

    // If subscription covers it, no coins needed
    if (isSubscriptionActive() && feature.subscriptionUnlimited) {
      toast.success(`Using ${feature.name} (included in subscription)`)
      return true
    }

    // Spend coins for the feature
    if (spendCoins(feature.cost, feature.name)) {
      toast.success(`Used ${feature.cost} RRAASI coins for ${feature.name}`)
      return true
    }

    toast.error('Failed to use feature')
    return false
  }, [canAccessFeature, isSubscriptionActive, spendCoins])

  // Add bonus coins (for testing or actual bonuses)
  const addBonusCoins = useCallback((amount: number, reason: string) => {
    addCoins(amount, 'bonus', reason)
    toast.success(`+${amount} RRAASI coins added! ${reason}`)
  }, [addCoins])

  // Get feature cost information
  const getFeatureInfo = useCallback((featureId: string) => {
    return getFeatureCost(featureId)
  }, [])

  // Check if user has enough coins for a specific amount
  const canAfford = useCallback((amount: number): boolean => {
    return hasEnoughCoins(amount)
  }, [hasEnoughCoins])

  // Get remaining coins after spending
  const getRemainingAfterSpend = useCallback((amount: number): number => {
    return Math.max(0, balance.total - amount)
  }, [balance.total])

  // Get coin statistics
  const getCoinStats = useCallback(() => {
    const totalEarned = balance.earned + balance.bonus + balance.subscription
    const totalSpent = balance.spent
    const currentBalance = balance.total
    
    return {
      totalEarned,
      totalSpent,
      currentBalance,
      spendingRate: totalEarned > 0 ? (totalSpent / totalEarned) * 100 : 0,
      averageTransaction: transactions.length > 0 ? totalSpent / transactions.filter(t => t.type === 'spent').length : 0
    }
  }, [balance, transactions])

  // Get recent transactions
  const getRecentTransactions = useCallback((limit: number = 5) => {
    return transactions.slice(0, limit)
  }, [transactions])

  // Check if user is a heavy spender
  const isHeavySpender = useCallback((): boolean => {
    const stats = getCoinStats()
    return stats.spendingRate > 80 // Spent more than 80% of earned coins
  }, [getCoinStats])

  // Get coin earning suggestions
  const getEarningSuggestions = useCallback(() => {
    const suggestions = []
    
    if (balance.total < 50) {
      suggestions.push('Complete daily login to earn 5 coins')
      suggestions.push('Refer friends to earn 25 coins each')
      suggestions.push('Upgrade subscription for bonus coins')
    }
    
    if (isHeavySpender()) {
      suggestions.push('Consider upgrading to premium for unlimited access')
      suggestions.push('Use free tier features to save coins')
    }
    
    return suggestions
  }, [balance.total, isHeavySpender])

  return {
    // State
    balance,
    transactions,
    
    // Core functions
    canAccessFeature,
    useFeature,
    addBonusCoins,
    getFeatureInfo,
    canAfford,
    getRemainingAfterSpend,
    
    // Statistics
    getCoinStats,
    getRecentTransactions,
    isHeavySpender,
    getEarningSuggestions,
    
    // Utility functions
    getBalance,
    getTransactionHistory,
    hasEnoughCoins
  }
}
